import { createReducer, on } from '@ngrx/store';
import {
    ILoadableArray,
    Loadable,
    onLoadableLoad,
    onLoadableSuccess,
    onLoadableError,
} from '../../models/loadable';
import { RuleScheduleActions } from './rule-schedule.actions';
import { initialRuleScheduleState } from './rule-schedule.state';
import { UserActions } from '../user/user.actions';

// ---------------------------------------------------------------------------
// Reducer helper functions
// ---------------------------------------------------------------------------
type Identifiable = { id: string };

/** Append a newly created item to a loadable array (preserving any extra fields on the collection). */
function appendItem<T extends Identifiable, C extends ILoadableArray<T>>(
    collection: C,
    item: T,
): C {
    return { ...collection, items: [...collection.items, item] };
}

/** Replace an existing item in-place by matching `id`. */
function replaceItem<T extends Identifiable, C extends ILoadableArray<T>>(
    collection: C,
    item: T,
): C {
    return {
        ...collection,
        items: collection.items.map(existing =>
            existing.id === item.id ? item : existing,
        ),
    };
}

/** Remove items whose `id` appears in the given list. */
function removeItems<T extends Identifiable, C extends ILoadableArray<T>>(
    collection: C,
    ids: string[],
): C {
    return {
        ...collection,
        items: collection.items.filter(existing => !ids.includes(existing.id)),
    };
}

/** Transition saving Loadable to loading. */
function savingLoad(saving: Loadable): Loadable {
    return onLoadableLoad(saving);
}

/** Transition saving Loadable to success. */
function savingSuccess(saving: Loadable): Loadable {
    return onLoadableSuccess(saving);
}

/** Transition saving Loadable to error. */
function savingError(saving: Loadable, error: Error): Loadable {
    return onLoadableError(saving, error);
}

export const ruleScheduleReducer = createReducer(
    initialRuleScheduleState,

    // -----------------------------------------------------------------------
    // Schedules
    // -----------------------------------------------------------------------
    on(RuleScheduleActions.loadSchedules, state => ({
        ...state,
        schedules: onLoadableLoad(state.schedules),
    })),
    on(RuleScheduleActions.loadSchedulesSuccess, (state, { schedules }) => ({
        ...state,
        schedules: { ...onLoadableSuccess(state.schedules), items: schedules },
    })),
    on(RuleScheduleActions.loadSchedulesFailure, (state, { error }) => ({
        ...state,
        schedules: onLoadableError(state.schedules, error),
    })),

    on(RuleScheduleActions.createSchedule, state => ({
        ...state,
        saving: savingLoad(state.saving),
    })),
    on(RuleScheduleActions.createScheduleSuccess, (state, { schedule }) => ({
        ...state,
        schedules: appendItem(state.schedules, schedule),
        saving: savingSuccess(state.saving),
    })),
    on(RuleScheduleActions.createScheduleFailure, (state, { error }) => ({
        ...state,
        saving: savingError(state.saving, error),
    })),

    on(RuleScheduleActions.updateSchedule, state => ({
        ...state,
        saving: savingLoad(state.saving),
    })),
    on(RuleScheduleActions.updateScheduleSuccess, (state, { schedule }) => ({
        ...state,
        schedules: replaceItem(state.schedules, schedule),
        ruleLinks: replaceItem(state.ruleLinks, schedule),
        saving: savingSuccess(state.saving),
    })),
    on(RuleScheduleActions.updateScheduleFailure, (state, { error }) => ({
        ...state,
        saving: savingError(state.saving, error),
    })),

    on(RuleScheduleActions.deleteSchedules, state => ({
        ...state,
        saving: savingLoad(state.saving),
    })),
    on(RuleScheduleActions.deleteSchedulesSuccess, (state, { ids }) => ({
        ...state,
        schedules: removeItems(state.schedules, ids),
        ruleLinks: removeItems(state.ruleLinks, ids),
        saving: savingSuccess(state.saving),
    })),
    on(RuleScheduleActions.deleteSchedulesFailure, (state, { error }) => ({
        ...state,
        saving: savingError(state.saving, error),
    })),

    // -----------------------------------------------------------------------
    // Rule links
    // -----------------------------------------------------------------------
    on(RuleScheduleActions.loadRuleSchedules, (state, { ruleId }) => ({
        ...state,
        ruleLinks: { ...onLoadableLoad(state.ruleLinks), items: [], ruleId },
    })),
    on(RuleScheduleActions.loadRuleSchedulesSuccess, (state, { ruleId, schedules }) => ({
        ...state,
        ruleLinks: { ...onLoadableSuccess(state.ruleLinks), items: schedules, ruleId },
    })),
    on(RuleScheduleActions.loadRuleSchedulesFailure, (state, { error }) => ({
        ...state,
        ruleLinks: { ...onLoadableError(state.ruleLinks, error), items: [] },
    })),
    on(RuleScheduleActions.clearRuleSchedules, state => ({
        ...state,
        ruleLinks: { ...initialRuleScheduleState.ruleLinks },
    })),

    // -----------------------------------------------------------------------
    // Link / unlink
    // -----------------------------------------------------------------------
    on(RuleScheduleActions.linkSchedules, RuleScheduleActions.unlinkSchedules, state => ({
        ...state,
        linking: savingLoad(state.linking),
    })),
    on(RuleScheduleActions.linkSchedulesSuccess, RuleScheduleActions.unlinkSchedulesSuccess, state => ({
        ...state,
        linking: savingSuccess(state.linking),
    })),
    on(RuleScheduleActions.linkSchedulesFailure, RuleScheduleActions.unlinkSchedulesFailure, (state, { error }) => ({
        ...state,
        linking: savingError(state.linking, error),
    })),

    // -----------------------------------------------------------------------
    // Reset on logout
    // -----------------------------------------------------------------------
    on(UserActions.logout, () => ({ ...initialRuleScheduleState })),
);
